import React, { useState, useCallback, useEffect, useMemo } from 'react';
import Box from '@mui/material/Box';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Avatar from '@mui/material/Avatar';
import { useGraphEditorStore } from '@/graph-store';
import { useSelectedIds } from '@/graph-store/hooks';
import { useToast } from '@/hooks/useToast';
import { isRootMaterial } from '@/graph/domain/topology';
import { getEditBlockedMessage } from '@/utils/editBlockedMessages';
import { TranslationPanel } from './TranslationPanel';
import { InfoTab, type SelectedCell } from './InfoTab';
import type { StepIconType } from './TranslationPanel/translation.types';
import type { OperationNodeSpec } from '@/graph/domain/types';
import type { GraphComment } from '@/graph-store/types';

const TRANSFER_EDGE_SUFFIXES = ['-transfer', '-remainder', '-dest'] as const;

const getOperationIdFromVisualEdgeId = (edgeId: string): string | null => {
  if (!edgeId.startsWith('op-edge-')) return null;

  let operationId = edgeId.substring('op-edge-'.length);
  for (const suffix of TRANSFER_EDGE_SUFFIXES) {
    if (operationId.endsWith(suffix)) {
      operationId = operationId.substring(0, operationId.length - suffix.length);
      break;
    }
  }

  return operationId || null;
};

export const RightPanel: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [activeProtocolIcon, setActiveProtocolIcon] = useState<{ nodeId: string; iconType: StepIconType } | null>(null);
  const [newComment, setNewComment] = useState('');

  const { showSuccess, showWarning } = useToast();
  const selectedIds = useSelectedIds();
  const selectedCellId = selectedIds.length === 1 ? selectedIds[0] : null;
  const isReadOnly = useGraphEditorStore((s) => s.ui.isReadOnly);
  const isProtocolLocked = useGraphEditorStore((s) => s.ui.isProtocolLocked ?? false);
  const isLifecycleLocked = useGraphEditorStore((s) => s.ui.isLifecycleLocked ?? false);
  const isArchived = useGraphEditorStore((s) => s.ui.isArchived ?? false);
  const isArchivalInProgress = useGraphEditorStore((s) => s.ui.isArchivalInProgress ?? false);
  const runsExist = useGraphEditorStore((s) => s.ui.runsExist ?? false);
  const canEditContent = useGraphEditorStore((s) => s.ui.canEditContent ?? true);
  const materialNodes = useGraphEditorStore((s) => s.graphData.materialNodes);
  const operationNodes = useGraphEditorStore((s) => s.graphData.operationNodes);
  const flowEdges = useGraphEditorStore((s) => s.graphData.flowEdges);
  const groups = useGraphEditorStore((s) => s.graphData.groups);
  const allComments = useGraphEditorStore((s) => s.graphData.graphComments);
  const addGraphComment = useGraphEditorStore((s) => s.addGraphComment);
  const currentUser = useGraphEditorStore((s) => s.ui.currentUser);

  // Find all operation IDs from selected IDs (for highlighting in translation panel)
  // For single step: highlights just that step
  // For transfer steps: all variants highlight the same operation
  // For group selections: highlights ALL operations in the group
  const highlightedOperationIds = useMemo(() => {
    const highlighted = new Set<string>();
    
    for (const id of selectedIds) {
      // If it's a direct operation node, add it
      if (operationNodes[id]) {
        highlighted.add(id);
      }
      // If it's an edge cell ID, extract the operation ID
      else if (typeof id === 'string' && id.startsWith('op-edge-')) {
        let operationId = id.substring('op-edge-'.length);
        
        // Try removing known transfer step suffixes
        const suffixes = ['-transfer', '-remainder', '-dest'];
        for (const suffix of suffixes) {
          if (operationId.endsWith(suffix)) {
            const baseId = operationId.substring(0, operationId.length - suffix.length);
            if (operationNodes[baseId]) {
              highlighted.add(baseId);
              operationId = ''; // Mark as processed
              break;
            }
          }
        }
        
        // Try without any suffix (normal operation edge)
        if (operationId && operationNodes[operationId]) {
          highlighted.add(operationId);
        }
      }
    }
    
    return highlighted;
  }, [selectedIds, operationNodes]);

  // Derive SelectedCell from the selected ID (or first selected ID for multi-select like transfer variants)
  const selectedCell: SelectedCell = useMemo(() => {
    // Use selectedCellId if available, otherwise try the first selected ID
    const cellIdToCheck = selectedCellId || (selectedIds.length > 0 ? selectedIds[0] : null);
    
    
    if (!cellIdToCheck) return null;

    if (materialNodes[cellIdToCheck]) {
      return { cellType: 'material', id: cellIdToCheck };
    }
    if (operationNodes[cellIdToCheck]) {
      return {
        cellType: 'operation',
        operationNodeId: cellIdToCheck,
        label: operationNodes[cellIdToCheck].displayName,
      };
    }
    if (groups[cellIdToCheck]) {
      return { cellType: 'group', id: cellIdToCheck };
    }
    // Visual operation edge IDs (op-edge-*) map back to operation details in Info tab.
    if (typeof cellIdToCheck === 'string') {
      const operationId = getOperationIdFromVisualEdgeId(cellIdToCheck);
      if (operationId && operationNodes[operationId]) {
        return {
          cellType: 'operation',
          operationNodeId: operationId,
          label: operationNodes[operationId].displayName,
        };
      }
    }
    // Fallback: check flowEdges (for direct edge IDs)
    if (flowEdges[cellIdToCheck]) {
      return { cellType: 'edge', id: cellIdToCheck };
    }

    return null;
  }, [selectedCellId, selectedIds, materialNodes, operationNodes, flowEdges, groups]);

  // Don't auto-switch tabs - let user stay on their selected tab
  // Users should be able to stay on Comments tab when selecting a step

  // Get comments for selected node (operation, material, or edge)
  const selectedNodeComments = useMemo(() => {
    if (!selectedCell) return [];

    let nodeId: string | null = null;

    if (selectedCell.cellType === 'operation') {
      nodeId = selectedCell.operationNodeId;
    } else if (selectedCell.cellType === 'material') {
      nodeId = selectedCell.id;
    } else if (selectedCell.cellType === 'edge') {
      // Extract operation ID from edge cell ID
      // Format: op-edge-<opId> or op-edge-<opId>-transfer/remainder/dest
      const edgeId = selectedCell.id;
      if (typeof edgeId === 'string') {
        nodeId = getOperationIdFromVisualEdgeId(edgeId);
      }
    }

    if (!nodeId) return [];

    return (allComments ?? []).filter((c) => c.stepId === nodeId);
  }, [selectedCell, allComments]);

  // Build a reverse index: operationNodeId -> edgeIds (memoized for performance)
  const operationToEdgesMap = useMemo(() => {
    
    const map = new Map<string, string[]>();
    for (const [edgeId, edge] of Object.entries(flowEdges)) {
      // An edge is related to an operation if either source or target is an operation node
      if (edge.sourceId && operationNodes[edge.sourceId]) {
        if (!map.has(edge.sourceId)) {
          map.set(edge.sourceId, []);
        }
        map.get(edge.sourceId)!.push(edgeId);
        
      }
      
      if (edge.targetId && operationNodes[edge.targetId]) {
        if (!map.has(edge.targetId)) {
          map.set(edge.targetId, []);
        }
        map.get(edge.targetId)!.push(edgeId);
        
      }
    }
    
    return map;
  }, [flowEdges, operationNodes]);

  const handleStepClicked = useCallback((nodeId: string) => {
    const state = useGraphEditorStore.getState();
    const { operationNodes, groups } = state.graphData;
    const selectedCells = new Set<string>();

    const addOperationSelection = (operationId: string) => {
      // TRANSFORM/MEASURE/TRANSFER visual edge IDs.
      selectedCells.add(`op-edge-${operationId}`);
      selectedCells.add(`op-edge-${operationId}-transfer`);
      selectedCells.add(`op-edge-${operationId}-remainder`);
      selectedCells.add(`op-edge-${operationId}-dest`);

      // COMPLEX steps render direct visual edges with store flow-edge IDs.
      const relatedFlowEdgeIds = operationToEdgesMap.get(operationId) ?? [];
      for (const edgeId of relatedFlowEdgeIds) {
        selectedCells.add(edgeId);
      }
    };
    
    // First check: is it a direct operation (including operations inside groups)?
    if (operationNodes[nodeId]) {
      // This handles standalone operations and individual steps inside groups.
      addOperationSelection(nodeId);
    }
    // Second check: is it a group ID (clicking the group header)?
    else if (groups[nodeId]) {
      // Group: select visual edges for every step in the group.
      const group = groups[nodeId];
      for (const stepId of group.stepIds) {
        if (operationNodes[stepId]) {
          addOperationSelection(stepId);
        }
      }
    }

    if (selectedCells.size > 0) {
      state.setSelection(Array.from(selectedCells));
    }
  }, [operationToEdgesMap]);

  const handleEditRequested = useCallback((nodeId: string) => {
    if (isProtocolLocked || isLifecycleLocked || isArchived || isArchivalInProgress || runsExist || canEditContent === false) {
      showWarning(getEditBlockedMessage({
        isProtocolLocked,
        isLifecycleLocked,
        isArchived,
        isArchivalInProgress,
        runsExist,
        canEditContent,
      }));
      return;
    }
    
    const state = useGraphEditorStore.getState();
    const isMaterial = Boolean(state.graphData.materialNodes[nodeId]);
    const isGroup = Boolean(state.graphData.groups[nodeId]);

    if (isMaterial) {
      if (!isRootMaterial(nodeId, state.graphData.flowEdges)) {
        showWarning('Downstream materials are derived from their source and cannot be edited.');
        return;
      }
      state.openModal({ kind: 'edit-material', payload: { materialId: nodeId } });
    } else if (isGroup) {
      state.openModal({ kind: 'edit-group', payload: { groupId: nodeId } });
    } else {
      state.openModal({ kind: 'edit-step', payload: { operationId: nodeId } });
    }
  }, [isProtocolLocked, isLifecycleLocked, isArchived, isArchivalInProgress, runsExist, canEditContent, showWarning]);

  const handleIconClicked = useCallback((nodeId: string, iconType: StepIconType) => {
    setActiveProtocolIcon((prev) => {
      if (prev && prev.nodeId === nodeId && prev.iconType === iconType) {
        return null;
      }
      return { nodeId, iconType };
    });
  }, []);

  // Add comment - for selected operation, material, or edge node
  const handleAddComment = useCallback(() => {
    if (isProtocolLocked || isLifecycleLocked || isArchived || isArchivalInProgress || runsExist ||canEditContent === false) {
      showWarning(getEditBlockedMessage({
        isProtocolLocked,
        isLifecycleLocked,
        isArchived,
        isArchivalInProgress,
        runsExist,
        canEditContent,
      }));
      return;
    }
    
    const text = newComment.trim();
    if (!text || !selectedCell) return;
    
    // Extract node ID from operation, material, or edge
    let nodeId: string | null = null;
    if (selectedCell.cellType === 'operation') {
      nodeId = selectedCell.operationNodeId;
    } else if (selectedCell.cellType === 'material') {
      nodeId = selectedCell.id;
    } else if (selectedCell.cellType === 'edge') {
      // Extract operation ID from edge cell ID
      // Format: op-edge-<opId> or op-edge-<opId>-transfer/remainder/dest
      const edgeId = selectedCell.id;
      if (typeof edgeId === 'string') {
        nodeId = getOperationIdFromVisualEdgeId(edgeId);
      }
    }

    if (!nodeId) return;

    const comment: GraphComment = {
      id: `comment-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      comment: nodeId,
      new: true,
      data: text,
      stepId: nodeId,
      addedBy: {
        _id: currentUser?._id,
        name: currentUser?.name || 'User',
        email: currentUser?.email || '',
        profilePhoto: currentUser?.profilePhoto || null,
      },
      timestamp: Date.now(),
      type: 'GRAPH',
    };

    // Local-only until Save sends allComments in /api/version/update payload.
    addGraphComment(comment);
    showSuccess('Comment added successfully');
    setNewComment('');
  }, [newComment, selectedCell, currentUser, addGraphComment, showSuccess, isProtocolLocked, isLifecycleLocked, isArchived, isArchivalInProgress, runsExist, showWarning]);

  // Format date/time for display
  const formatDate = (timestamp: number): string => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Handle parameter changes (for notes and parameters in Translation tab)
  const handleParameterChanged = useCallback((nodeId: string, paramName: string, value: unknown) => {
    const state = useGraphEditorStore.getState();
    const operation = state.graphData.operationNodes[nodeId];
    if (!operation) return;

    const patch: Partial<OperationNodeSpec> = {};
    
    // Handle special case: notes field
    if (paramName === 'notes') {
      patch.notes = typeof value === 'string' ? value : String(value ?? '');
    } else {
      // Handle all other parameters (tables, fields, dropdowns, etc.)
      patch.parameters = {
        ...operation.parameters,
        [paramName]: value,
      };
    }

    if (Object.keys(patch).length > 0) {
      state.editStep(nodeId, patch);
    }
  }, []);

  // Auto-expand transfer step selections to include all edge variants
  // Transfer steps have multiple edges: main + transfer + remainder + dest
  // When selecting any one, we should select all of them for complete highlighting
  // Also handles group selections: expands all operations in the group to include their transfer variants
  useEffect(() => {
    if (selectedIds.length === 0) return;
    
    // Collect all operation IDs from selected edges
    const operationsToExpand = new Set<string>();
    
    for (const id of selectedIds) {
      if (typeof id === 'string' && id.startsWith('op-edge-')) {
        const operationId = getOperationIdFromVisualEdgeId(id);
        if (!operationId) continue;

        if (operationNodes[operationId]?.archetype === 'TRANSFER') {
          operationsToExpand.add(operationId);
        }
      }
    }
    
    // If we found operations to expand, check if we need to add their transfer variants
    if (operationsToExpand.size > 0) {
      // Build the full set of expected variants
      const expectedVariants = new Set<string>();
      for (const opId of operationsToExpand) {
        expectedVariants.add(`op-edge-${opId}`);
        expectedVariants.add(`op-edge-${opId}-transfer`);
        expectedVariants.add(`op-edge-${opId}-remainder`);
        expectedVariants.add(`op-edge-${opId}-dest`);
      }
      
      // Check if all variants are already selected (prevent infinite loop)
      const allVariantsSelected = Array.from(expectedVariants).every(v => selectedIds.includes(v));
      
      if (!allVariantsSelected) {
        useGraphEditorStore.getState().setSelection(Array.from(expectedVariants));
      }
    }
  }, [selectedIds, operationNodes]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        borderLeft: '1px solid #e5e7eb',
      }}
    >
      <Tabs
        value={activeTab}
        onChange={(_, v) => setActiveTab(v)}
        variant="fullWidth"
        sx={{
          flexShrink: 0,
          borderBottom: '1px solid #e5e7eb',
          '& .MuiTab-root': {
            minHeight: 0,
            py: 0.75,
            textTransform: 'none',
            fontSize: '0.75rem',
            fontWeight: 500,
            flexDirection: 'column',
            color: '#0068ce',
          },
          '& .MuiTab-iconWrapper': {
            marginBottom: 0,
            marginRight: 0,
            fontSize: '1rem',
            color: '#0068ce',
          },
        }}
      >
        <Tab
          label="protocol"
          icon={<i className="las la-scroll" aria-hidden="true" style={{ fontSize: '1.1rem' }} />}
          iconPosition="top"
        />
        <Tab
          label="comments"
          icon={<i className="las la-comments" aria-hidden="true" style={{ fontSize: '1.1rem' }} />}
          iconPosition="top"
        />
        <Tab
          label="info"
          icon={<i className="las la-info-circle" aria-hidden="true" style={{ fontSize: '1.1rem' }} />}
          iconPosition="top"
        />
      </Tabs>

      <Box sx={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
        {activeTab === 0 && (
          <TranslationPanel
            editable={!isReadOnly}
            highlightedStepIds={highlightedOperationIds}
            activeIcon={activeProtocolIcon}
            onStepClicked={handleStepClicked}
            onStepEditRequested={handleEditRequested}
            onIconClicked={handleIconClicked}
            onParameterChanged={handleParameterChanged}
          />
        )}
        {activeTab === 1 && (
          <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', height: '100%', gap: 0 }}>
            {/* Comments display section */}
            <Box sx={{ flex: 1, overflowY: 'auto', minHeight: 0, mb: 2 }}>
              {!selectedCell || (selectedCell.cellType !== 'operation' && selectedCell.cellType !== 'material' && selectedCell.cellType !== 'edge') ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                  Select a step, edge, or material to view and add comments.
                </Typography>
              ) : selectedNodeComments.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                  No comments yet.
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {selectedNodeComments.map((comment) => (
                    <Paper
                      key={comment.id}
                      sx={{
                        p: 1.5,
                        bgcolor: '#f8fafc',
                        border: '1px solid #e5e7eb',
                        borderRadius: 1,
                      }}
                    >
                      {/* User info row: profile image + name + date/time */}
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
                        <Avatar
                          src="/assets/images/user-profile.png"
                          alt={comment.addedBy.name}
                          sx={{
                            width: 28,
                            height: 28,
                            fontSize: '0.75rem',
                            bgcolor: '#9ca3af',
                          }}
                        >
                          {(comment.addedBy.name || 'U').charAt(0).toUpperCase()}
                        </Avatar>
                        <Box sx={{ flex: 1 }}>
                          <Typography
                            variant="caption"
                            sx={{
                              display: 'block',
                              fontWeight: 600,
                              color: '#1f2937',
                            }}
                          >
                            {comment.addedBy.name}
                          </Typography>
                        </Box>
                        <Typography
                          variant="caption"
                          sx={{
                            color: '#9ca3af',
                            fontSize: '0.75rem',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {formatDate(comment.timestamp)}
                        </Typography>
                      </Box>

                      {/* Comment text */}
                      <Typography
                        variant="body2"
                        sx={{
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                          color: '#374151',
                          fontSize: '0.875rem',
                          lineHeight: 1.5,
                        }}
                      >
                        {comment.data}
                      </Typography>
                    </Paper>
                  ))}
                </Box>
              )}
            </Box>

            {/* Input form - always visible, post button conditionally disabled */}
            <Box sx={{ borderTop: '1px solid #e5e7eb', pt: 2, mt: 'auto' }}>
              <Box
                component="form"
                noValidate
                onSubmit={(e) => {
                  e.preventDefault();
                  handleAddComment();
                }}
              >
                {/* Textarea input - always visible */}
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  maxRows={4}
                  placeholder="Enter Comment"
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  size="small"
                  variant="outlined"
                  sx={{
                    mb: 1,
                    '& .MuiOutlinedInput-root': {
                      fontSize: '0.875rem',
                    },
                  }}
                />

                {/* Post button - always visible but disabled based on conditions */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  {(() => {
                    const hasComment = newComment.trim().length > 0;
                    const isNotReadOnly = !isReadOnly;
                    const hasCellSelected = Boolean(selectedCell);
                    const isValidCellType = selectedCell && 
                      (selectedCell.cellType === 'operation' || 
                       selectedCell.cellType === 'material' || 
                       selectedCell.cellType === 'edge');
                    
                    const isDisabled = !hasComment || !isNotReadOnly || !hasCellSelected || !isValidCellType;

                    return (
                      <Button
                        type="submit"
                        variant="outlined"
                        color="primary"
                        size="small"
                        disabled={isDisabled}
                        sx={{
                          textTransform: 'none',
                          paddingX: 2,
                        }}
                      >
                        Post
                      </Button>
                    );
                  })()}
                </Box>
              </Box>
            </Box>
          </Box>
        )}
        {activeTab === 2 && (
          <InfoTab selectedCell={selectedCell} onEditRequested={handleEditRequested} />
        )}
      </Box>
    </Box>
  );
};
